import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';

import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';

import { selectPermissions } from 'selectors';
import {
  hasAccessToPathname as hasAccessToPathnameUtil,
  hasAccessToProduct as hasAccessToProductUtil,
} from 'utils/permissions';

const usePermissions = () => {
  const permissions = useSelector(selectPermissions, isEqual);

  /**
   * Check if the current user has access to a product/s
   *
   * Note that users with `salesforceData.respectProvisioning = false` will have
   * access to all provisioning available. This doesn't apply for rollouts.
   *
   * @param value provisioning, rollout, or a list of products
   * @param arrayMode logic to apply in case we pass an array with different values
   *
   * @example
   * // Check if the user has access to Chat & ExternalID:
   * const { hasAccessToProduct } = usePermissions();
   * hasAccessToProduct([ProvisioningEnum.Chat, RolloutEnum.ExternalId], 'every');
   */
  const hasAccessToProduct = useCallback(
    (
      value?: ProvisioningEnum | RolloutEnum | (ProvisioningEnum | RolloutEnum)[],
      arrayMode: 'some' | 'every' = 'some',
    ) => !!permissions && hasAccessToProductUtil(permissions, value, arrayMode),
    [permissions],
  );

  const hasAccessToPathname = useCallback(
    (pathname: string) => !!permissions && hasAccessToPathnameUtil(permissions, pathname),
    [permissions],
  );

  return {
    permissions,
    hasAccessToProduct,
    hasAccessToPathname,
  };
};

export default usePermissions;
